import { useState, useEffect, useMemo } from "react";
import { useMedia } from "@/contexts/MediaContext";
import { useModel } from "@/contexts/ModelContext";
import { SidebarActions } from "@/components/layout/SidebarActions";
import { SidebarHeader } from "@/components/layout/SidebarHeader";
import { PromptLabelList } from "../PromptLabel/PromptLabelList";
import { useFlow } from "@/contexts/FlowContext";
import { DEFAULT_CHILD_ANIMATION_DELAY, DEFAULT_DURATION } from "@/constants/motion";
import { MediaItemStatus } from "@/types/media";
import { GeneratePredictions } from "./GeneratePredictions";
import { BuildModelBtn } from "./BuildModelBtn";
import { BuildPromptSteps } from "@/types/flows";
import { useTextPromptMode, useCurrentMedia, useMediaStore } from "@/store";
import { ToggleTextPromptModeBtn } from "./ToggleTextPromptModeBtn";
import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";
import { getExtractedFrameIdsForMediaItem } from "@/hooks/useMediaUtils";
import { useComputedValues } from "@/contexts/ComputedValuesContext";

export const PromptAnnotateDataSidebarContent = () => {
    const { annotationsForFileAndFrameId, approvedFileIds } = useModel();
    const { flowLocked } = useFlow();
    const isGeneratingPredictions = useIsGeneratingPredictions();
    const { pendingMediaItems, mediaItems } = useMedia();
    const media = useMediaStore();
    const currentMedia = useCurrentMedia();
    const textPromptMode = useTextPromptMode();
    const { currentFrameAnnotations } = useComputedValues();

    const [showCTAs, setShowCTAs] = useState(false);

    // Show CTAs after parent animation completes
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowCTAs(true);
        }, DEFAULT_CHILD_ANIMATION_DELAY); // Slightly longer than parent animation

        return () => clearTimeout(timer);
    }, []);

    // Check if there are any confirmed (non-draft) annotations
    const hasConfirmedAnnotations = useMemo(() => {
        const allAnnotationsAcrossFiles = Object.values(annotationsForFileAndFrameId).flatMap(
            (annotationsForFile) => Object.values(annotationsForFile).flat()
        );

        // Check if there are any annotations that are not in draft state
        return allAnnotationsAcrossFiles.some((anno) => anno && !anno.draft);
    }, [annotationsForFileAndFrameId]);

    // Check if user has approved predictions (has annotations that came from predictions)
    const hasApprovedPredictions = useMemo(() => {
        // Check if the current file has been approved (user clicked "approve" after generating predictions)
        const currentFileApproved =
            currentMedia?.file.id && approvedFileIds.has(currentMedia?.file.id);

        // If file was approved, also check if current frame has any confirmed annotations
        // If user deleted all boxes, revert to initial state
        if (currentFileApproved) {
            const hasConfirmedAnnotationsOnCurrentFrame = currentFrameAnnotations.some(
                (anno) => !anno.draft
            );
            return hasConfirmedAnnotationsOnCurrentFrame;
        }

        return false;
    }, [currentMedia?.file.id, approvedFileIds, currentFrameAnnotations]);

    // Check if current file has confirmed annotations
    const currentFileHasConfirmedAnnotations = useMemo(() => {
        return currentFrameAnnotations.some((anno) => !anno.draft);
    }, [currentFrameAnnotations]);

    // Determine if this is a new file (has confirmed annotations elsewhere but not on current file)
    const isNewFileWithoutAnnotations = useMemo(() => {
        return hasConfirmedAnnotations && !currentFileHasConfirmedAnnotations;
    }, [hasConfirmedAnnotations, currentFileHasConfirmedAnnotations]);

    // Check if user has uploaded multiple files
    const hasMultipleFiles = useMemo(() => {
        return mediaItems.length > 1;
    }, [mediaItems]);

    // Check if files are still being uploaded/processed
    const hasFilesUploading = useMemo(() => {
        return (
            pendingMediaItems.length > 0 ||
            mediaItems.some((item) => {
                const extractedFrameIds = getExtractedFrameIdsForMediaItem(item);
                const uploadingExtractedFrames = extractedFrameIds.some(
                    (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
                );

                return (
                    item.status === MediaItemStatus.UPLOADING ||
                    item.status === MediaItemStatus.PROCESSING ||
                    uploadingExtractedFrames
                );
            })
        );
    }, [media, pendingMediaItems, mediaItems]);

    const editBoxesDescription = textPromptMode ? "Add a new prompt" : "Edit the boxes";

    return (
        <>
            <SidebarHeader
                title={
                    isNewFileWithoutAnnotations
                        ? `Click "Find Objects" to locate the objects in this ${currentMedia?.file.isVideo ? "frame" : "file"}.`
                        : hasApprovedPredictions
                          ? hasMultipleFiles
                              ? `${editBoxesDescription}, go to the next file, or click "Review Model" if everything looks good.`
                              : `${editBoxesDescription} or click "Review Model" if everything looks good.`
                          : "What object are you looking for?"
                }
                description={
                    hasApprovedPredictions ||
                    textPromptMode ||
                    isNewFileWithoutAnnotations ? null : (
                        <p>
                            Tell us below or draw a box{" "}
                            <span>
                                <i className="far fa-vector-square mx-0.5"></i>
                            </span>{" "}
                            around what you want to find.
                        </p>
                    )
                }
            />
            <div className="content">
                <GeneratePredictions hideTextPrompt={hasConfirmedAnnotations} />
                <PromptLabelList mode={BuildPromptSteps.ANNOTATE_DATA} />
            </div>
            <SidebarActions
                toolbarProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: showCTAs ? 1 : 0 },
                    transition: { duration: DEFAULT_DURATION.slower, ease: "easeOut" }
                }}
            >
                <ToggleTextPromptModeBtn disabled={isGeneratingPredictions} />
                <BuildModelBtn
                    disabled={
                        flowLocked ||
                        hasFilesUploading ||
                        !hasConfirmedAnnotations ||
                        isGeneratingPredictions
                    }
                    loading={hasFilesUploading}
                />
            </SidebarActions>
        </>
    );
};
